import { useEffect, useMemo, useState } from "react";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, Outlet, useNavigate } from "react-router-dom";

import DisableSwipeBack from "./DisableSwipe";

import ErrorPage from "@/components/state-pages/ErrorPage";
import LoadingScreen from "@/components/state-pages/LoadingScreen";
import { SOCKET_KEYS } from "@/constants/socket.constant";
import { useApiInvalidator } from "@/hooks/useApiInvalidator";
import { useSocket } from "@/hooks/useSocket";
import MainLayout from "@/layouts/MainLayout";
import { isAdminDomain, isSubDomain } from "@/lib/utils";
import {
  useGetSingleWorkspaceQuery,
  workspaceApi,
} from "@/redux/api/workspaceQuery";
import {
  refetchWorkspaceData,
  refetchWorkspaceSyncedWithXero,
} from "@/redux/slices/userSlice";
import { RootState } from "@/redux/store";
import { getSubdomain, getWorkspace } from "@/utils/helpers";

type TProps = {
  redirectPath?: string;
};

type SocketData = {
  workspaceId: string;
  action: "create" | "update" | "delete";
};

const ProtectedRoute: React.FC<TProps> = ({ redirectPath = "/login" }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isInitialized, setIsInitialized] = useState(false);

  const userData = useSelector(
    (state: RootState) => state?.user?.userData || "",
  );
  const { socket } = useSocket("/");
  const { invalidateTags } = useApiInvalidator<typeof workspaceApi>();

  const skipQuery = isAdminDomain || !getSubdomain();

  const {
    data: otherData,
    isSuccess,
    isLoading,
    isError,
  } = useGetSingleWorkspaceQuery(getSubdomain() ?? "", {
    skip: skipQuery,
  });

  // Handle workspace initialization
  useEffect(() => {
    if (skipQuery) {
      // For admin domain, mark as initialized immediately
      setIsInitialized(true);
    } else if (isSuccess) {
      // For non-admin domains, initialize after successful query
      const isAdminDomainImage = isAdminDomain ? "/ald-logo.svg" : "/fav.png";
      const workspaceData = {
        ...otherData,
        logo: otherData?.logo?.startsWith("http")
          ? otherData.logo
          : isAdminDomainImage,
      };

      localStorage.setItem("workspace", JSON.stringify(otherData?.id || ""));
      localStorage.setItem("workspaceData", JSON.stringify(workspaceData));

      dispatch(refetchWorkspaceData());
      dispatch(refetchWorkspaceSyncedWithXero());

      setIsInitialized(true);
    }
  }, [isSuccess, otherData, skipQuery, dispatch]);

  // Redirect if no user data
  useEffect(() => {
    if (!userData) {
      localStorage.clear();
      const [subdomain] = window.location.hostname.split(".");
      localStorage.setItem("subdomain", subdomain);
      navigate("/login");
    }
  }, [userData, navigate]);

  // Setup socket listener
  useEffect(() => {
    if (!socket) {
      return;
    }

    const handleWorkspaceEvent = (data: SocketData): void => {
      if (
        data.action === "update" &&
        data.workspaceId === getWorkspace() &&
        isSubDomain
      ) {
        invalidateTags(workspaceApi, ["singleWorkspace"]);
      }
    };

    socket.on(SOCKET_KEYS.WORKSPACE, handleWorkspaceEvent);

    // eslint-disable-next-line consistent-return
    return (): void => {
      socket.off(SOCKET_KEYS.WORKSPACE, handleWorkspaceEvent);
    };
  }, [socket, invalidateTags]);

  // Determine if we should show loading state
  const shouldShowLoading = useMemo(() => {
    if (skipQuery) {
      return false;
    } // Admin domains don't need workspace data

    return isLoading || !isInitialized;
  }, [isLoading, isInitialized, skipQuery]);

  const hasValidIcon = useMemo(() => {
    return (
      otherData?.logo &&
      otherData.logo !== "undefined" &&
      otherData.logo !== "null" &&
      otherData.logo !== ""
    );
  }, [otherData]);

  // Redirect if no user data
  if (!userData) {
    return <Navigate to={redirectPath} replace />;
  }

  // Show loading screen if needed
  if (shouldShowLoading) {
    return <LoadingScreen />;
  }

  // Show error page if query failed
  if (isError) {
    return <ErrorPage />;
  }

  // Get title and favicon based on domain type
  const title = skipQuery ? "Admin Dashboard" : otherData?.name || "Workspace";

  const getFavicon = (): string => {
    if (skipQuery) {
      return "/ald-logo.svg";
    }

    if (hasValidIcon) {
      return otherData?.logo ?? "/ald-logo.svg";
    }

    return "/fav.png";
  };

  const favicon = getFavicon();

  return (
    <MainLayout>
      <HelmetProvider>
        <Helmet>
          <title>{title}</title>
          <link rel='icon' href={favicon} sizes='16x16' />
        </Helmet>
      </HelmetProvider>
      <DisableSwipeBack />
      <Outlet />
    </MainLayout>
  );
};

export default ProtectedRoute;
